import React, { useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { logError } from '../utils/log';
import SmsConsentCheckbox from '../components/SmsConsentCheckbox';
// Replaced lucide-react imports with FontAwesome equivalents in the render

const ReferralPage: React.FC = () => {
  // Changed default active tab to 'network'
  const [activeTab, setActiveTab] = useState<'referral' | 'network'>('network');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    businessName: '',
    contactName: '',
    email: '',
    phone: '',
    role: 'Veterinarian',
    patientName: '',
    ownerName: '',
    patientPhone: '', // Added patient phone
    patientEmail: '', // Added patient email
    clinicalNotes: '',
    additionalInfo: '', // Added additional info for network
    smsConsent: false,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const { smsConsent, ...referralData } = formData;
      const smsConsentNote = `SMS consent checkbox: ${smsConsent ? 'checked' : 'not checked'}.`;

      await addDoc(collection(db, 'referrals'), {
        ...referralData,
        additionalInfo: [
          referralData.additionalInfo,
          activeTab === 'network' ? smsConsentNote : '',
        ].filter(Boolean).join('\n\n'),
        clinicalNotes: [
          referralData.clinicalNotes,
          activeTab === 'referral' ? smsConsentNote : '',
        ].filter(Boolean).join('\n\n'),
        type: activeTab, // 'referral' or 'network'
        submittedAt: serverTimestamp(),
        status: 'new'
      });
      setSubmitted(true);
    } catch (err) {
      logError('Error submitting referral:', err);
      setError('Something went wrong. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 pt-32 pb-12 px-4 flex items-center justify-center">
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-lg w-full text-center border-2 border-brand-teal/20">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="fas fa-check-circle text-3xl text-green-600"></i>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Received!</h2>
          <p className="text-gray-600 mb-8">
            {activeTab === 'referral' 
              ? "We have received your patient referral. Our care coordinator will reach out to the pet owner within 1 business day."
              : "Thanks for joining our network! We look forward to collaborating with you."}
          </p>
          <button 
            onClick={() => window.location.reload()}
            className="text-brand-teal font-bold hover:underline"
          >
            Submit another
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Desktop Grid Layout */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start max-w-6xl mx-auto">
          
          {/* Left Column: Content */}
          <div className="text-center lg:text-left animate-fadeIn">
            <span className="text-brand-teal font-bold tracking-wider uppercase text-base md:text-sm block mb-3 md:mb-2">For Veterinarians & Pet Professionals</span>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mt-2 mb-6 font-nantes">
              Refer a Case to CHOMP
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed font-founders">
              We're South Florida's first clinic 100% dedicated to veterinary dentistry. 
              We keep you in the loop and send post-op reports.
            </p>
            
            <div className="grid sm:grid-cols-3 lg:grid-cols-1 gap-4 text-left text-base md:text-sm mb-8 lg:mb-0">
              <div className="bg-white p-6 md:p-4 lg:p-6 rounded-xl shadow-sm border border-gray-100 flex flex-col items-center text-center sm:items-start sm:text-left lg:flex-row lg:items-center lg:gap-4">
                <i className="fas fa-stethoscope text-3xl md:text-2xl text-brand-teal mb-3 md:mb-2 lg:mb-0 flex-shrink-0"></i>
                <span className="font-semibold text-gray-800 text-lg md:text-base">Entire focus on veterinary dental</span>
              </div>
              <div className="bg-white p-6 md:p-4 lg:p-6 rounded-xl shadow-sm border border-gray-100 flex flex-col items-center text-center sm:items-start sm:text-left lg:flex-row lg:items-center lg:gap-4">
                <i className="fas fa-clipboard-list text-3xl md:text-2xl text-brand-teal mb-3 md:mb-2 lg:mb-0 flex-shrink-0"></i>
                <span className="font-semibold text-gray-800 text-lg md:text-base">Same-day treatment options available</span>
              </div>
              <div className="bg-white p-6 md:p-4 lg:p-6 rounded-xl shadow-sm border border-gray-100 flex flex-col items-center text-center sm:items-start sm:text-left lg:flex-row lg:items-center lg:gap-4">
                <i className="fas fa-users text-3xl md:text-2xl text-brand-teal mb-3 md:mb-2 lg:mb-0 flex-shrink-0"></i>
                <span className="font-semibold text-gray-800 text-lg md:text-base">Fast communication + transparent pricing</span>
              </div>
            </div>
          </div>

          {/* Right Column: Form */}
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100 w-full">
            
            {/* Toggle Header */}
            <div className="flex border-b border-gray-100">
              <button
                onClick={() => setActiveTab('network')}
                className={`flex-1 py-5 text-center font-bold text-lg transition-colors ${
                  activeTab === 'network' 
                    ? 'bg-brand-teal text-white' 
                    : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                }`}
              >
                JOIN NETWORK
              </button>
              <button
                onClick={() => setActiveTab('referral')}
                className={`flex-1 py-5 text-center font-bold text-lg transition-colors ${
                  activeTab === 'referral' 
                    ? 'bg-brand-teal text-white' 
                    : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                }`}
              >
                REFER A PATIENT
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 md:p-8 space-y-6">
              
              {/* Professional Info - Always Visible */}
              <div className="space-y-5">
                <h3 className="text-xl font-bold text-gray-900 border-b pb-2 mb-4">Your Information</h3>
                
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-base font-medium text-gray-700 mb-2">Clinic / Business Name</label>
                    <input
                      required
                      name="businessName"
                      value={formData.businessName}
                      onChange={handleChange}
                      className="w-full px-4 py-3 text-base rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand-teal focus:border-transparent bg-white"
                      placeholder="e.g. Sunrise Animal Hospital"
                    />
                  </div>
                  <div>
                    <label className="block text-base font-medium text-gray-700 mb-2">Your Role</label>
                    <div className="relative">
                      <select
                        name="role"
                        value={formData.role}
                        onChange={handleChange}
                        className="w-full px-4 py-3 text-base rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand-teal focus:border-transparent bg-white appearance-none"
                      >
                        <option>Veterinarian</option>
                        <option>Vet Tech / Nurse</option>
                        <option>Groomer</option>
                        <option>Trainer</option>
                        <option>Other</option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-700">
                        <i className="fas fa-chevron-down text-sm"></i>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-base font-medium text-gray-700 mb-2">Contact Name</label>
                    <input
                      required
                      name="contactName"
                      value={formData.contactName}
                      onChange={handleChange}
                      className="w-full px-4 py-3 text-base rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand-teal focus:border-transparent bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-base font-medium text-gray-700 mb-2">Email</label>
                    <input
                      required
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 text-base rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand-teal focus:border-transparent bg-white"
                    />
                  </div>
                </div>
                
                <div>
                    <label className="block text-base font-medium text-gray-700 mb-2">Phone Number</label>
                    <input
                      required
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 text-base rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand-teal focus:border-transparent bg-white"
                    />
                  </div>

                <SmsConsentCheckbox
                  id="referral-sms-consent"
                  checked={formData.smsConsent}
                  onChange={handleChange}
                />
              </div>

              {/* Network Only Fields - Additional Info */}
              {activeTab === 'network' && (
                <div className="space-y-5 pt-6 animate-fadeIn">
                   <div>
                    <label className="block text-base font-medium text-gray-700 mb-2">Additional Info (Optional)</label>
                    <textarea
                      name="additionalInfo"
                      value={formData.additionalInfo}
                      onChange={handleChange}
                      rows={4}
                      className="w-full px-4 py-3 text-base rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand-teal focus:border-transparent bg-white"
                      placeholder="Any specific questions or details about your practice..."
                    />
                  </div>
                </div>
              )}

              {/* Patient Info - Only visible if Referring */}
              {activeTab === 'referral' && (
                <div className="space-y-4 pt-6 animate-fadeIn">
                  <h3 className="text-lg font-bold text-gray-900 border-b pb-2">Patient Details</h3>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Patient Name</label>
                      <input
                        required={activeTab === 'referral'}
                        name="patientName"
                        value={formData.patientName}
                        onChange={handleChange}
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-teal focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Owner Last Name</label>
                      <input
                        required={activeTab === 'referral'}
                        name="ownerName"
                        value={formData.ownerName}
                        onChange={handleChange}
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-teal focus:border-transparent"
                      />
                    </div>
                  </div>

                  {/* Patient Contact Info */}
                   <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Patient Phone</label>
                      <input
                        required={activeTab === 'referral'}
                        type="tel"
                        name="patientPhone"
                        value={formData.patientPhone}
                        onChange={handleChange}
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-teal focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Patient Email</label>
                      <input
                        required={activeTab === 'referral'}
                        type="email"
                        name="patientEmail"
                        value={formData.patientEmail}
                        onChange={handleChange}
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-teal focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Reason for Referral / Notes</label>
                    <textarea
                      required={activeTab === 'referral'}
                      name="clinicalNotes"
                      value={formData.clinicalNotes}
                      onChange={handleChange}
                      rows={4}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-teal focus:border-transparent"
                      placeholder="Brief history, specific concerns, or what you'd like us to evaluate..."
                    />
                  </div>
                </div>
              )}

              {error && (
                <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm text-center">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-brand-teal text-white font-bold text-lg py-4 rounded-xl hover:bg-teal-700 transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Sending...' : activeTab === 'referral' ? 'Send Referral' : 'Join Network'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferralPage;